import { useEffect, useState } from 'react';

/**
 * Tiny async-data hook. Call any api.* function and get { data, loading, error, refresh }.
 *
 * Usage:
 *   const { data: events, loading } = useApi(api.getEvents);
 *   const { data: event, loading } = useApi(api.getEvent, [id]);
 *
 * Args determine the dependency key (re-runs when args change).
 */
export function useApi(fn, args = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);

  const argsKey = JSON.stringify(args);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.resolve()
      .then(() => fn(...args))
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [argsKey, tick]);

  return {
    data,
    loading,
    error,
    refresh: () => setTick((t) => t + 1),
  };
}
